/**
 * Centralized date & tax-year validation for the entire app.
 *
 * Every field declares its preset ("taxYear", "softFutureDate", "recordDate")
 * and inherits the correct bounds. All bounds derive from `new Date()` —
 * nothing is hardcoded to a specific calendar year.
 *
 * Returns:
 *   { error: string|null, warning: string|null }
 *   error   → RED inline, blocks save (impossible value)
 *   warning → AMBER "Is that right?" confirm (unlikely but possible)
 */

// ── Constants (single source of truth) ──────────────────────────────
const FLOOR_YEAR = 1996; // Section 529 enacted — nothing predates it
const STALE_YEARS = 7;   // >7 years back → amber confirm
const FORWARD_HORIZON = 30; // completionDate: max years into the future (catches typos like 3026)
const FORWARD_WARN_YEARS = 5; // completionDate: amber confirm if more than 5 years out

function now() { return new Date(); }
// ONE CLOCK WITH THE SERVER. The bounds below are also enforced server-side
// (database.validate_date_field), which reads the year in UTC. Reading it in the
// browser's own time zone made the two disagree for the hours around New Year:
// west of UTC the browser is still in the old year and refuses a date the server
// accepts, east of it the browser offers a year the server rejects. Same value,
// two clocks — and the family sees a date field that contradicts the save. The
// wall-clock DAY below stays local on purpose: "today" for the person typing is
// their today, and that bound is a soft warning, not a refusal.
// (Pre-merge review, round 7.)
function currentYear() { return now().getUTCFullYear(); }
function todayIso() { const d = now(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}`; }

// ── Tax-year validation ─────────────────────────────────────────────
/**
 * Validate a tax year number.
 * @param {number} year
 * @param {object} opts
 * @param {number} [opts.assignedTaxYear] — for date↔year mismatch check
 * @returns {{ error: string|null, warning: string|null }}
 */
export function validateTaxYear(year, opts = {}) {
  const cy = currentYear();
  const ceiling = cy + 1;

  if (!Number.isFinite(year) || !Number.isInteger(year)) {
    return { error: "Enter a valid 4-digit year.", warning: null };
  }
  if (year < FLOOR_YEAR) {
    return { error: `Year must be ${FLOOR_YEAR} or later (Section 529 was enacted in 1996).`, warning: null };
  }
  if (year > ceiling) {
    return { error: `Year cannot exceed ${ceiling}.`, warning: null };
  }

  // Soft: stale
  if (year < cy - STALE_YEARS) {
    return { error: null, warning: `${year} is more than ${STALE_YEARS} years ago. Is that right?` };
  }
  return { error: null, warning: null };
}


// ── Full-date validation ────────────────────────────────────────────
/**
 * Validate an ISO yyyy-mm-dd date string.
 * @param {string} iso — "yyyy-mm-dd"
 * @param {object} opts
 * @param {"recordDate"|"completionDate"|"softFutureDate"} [opts.preset]
 *   "softFutureDate"  — warns on future dates (amber confirm, still saves)
 *   "completionDate"  — forward-looking (e.g. graduation); no currentYear+1 ceiling,
 *                        allows far-future, warns if >5 years out
 *   undefined/"recordDate" — general 529 date with currentYear+1 ceiling
 * @param {number} [opts.assignedTaxYear] — for date↔year mismatch check
 * @returns {{ error: string|null, warning: string|null }}
 */
export function validateDate(iso, opts = {}) {
  if (!iso) return { error: null, warning: null }; // empty is handled by required-field logic

  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!m) return { error: "Invalid date format.", warning: null };

  const y = +m[1], mo = +m[2], da = +m[3];

  // Check 4-digit year
  if (y < 1000 || y > 9999) {
    return { error: "Enter a valid 4-digit year.", warning: null };
  }

  // Check valid month/day (JS Date will roll over invalid combos)
  const probe = new Date(y, mo - 1, da);
  if (probe.getFullYear() !== y || probe.getMonth() !== mo - 1 || probe.getDate() !== da) {
    return { error: "Invalid date (check month/day).", warning: null };
  }

  const cy = currentYear();
  const isCompletion = opts.preset === "completionDate";

  // 529 floor
  if (y < FLOOR_YEAR) {
    return { error: `Date must be in ${FLOOR_YEAR} or later (Section 529 was enacted in 1996).`, warning: null };
  }

  // Year ceiling — different for completion dates vs. everything else
  if (isCompletion) {
    if (y > cy + FORWARD_HORIZON) {
      return { error: `Year cannot exceed ${cy + FORWARD_HORIZON}.`, warning: null };
    }
  } else {
    const ceiling = cy + 1;
    if (y > ceiling) {
      return { error: `Year cannot exceed ${ceiling}.`, warning: null };
    }
  }

  // There is deliberately NO hard future-date block here any more.
  //
  // A "transactionDate" preset used to reject any date after today outright,
  // which quietly disabled a feature the rest of the product is built for:
  // a future-dated expense shows an estimated exchange rate marked "Awaiting
  // final exchange rate", refreshes while the date is ahead, and locks once the
  // date passes (expense_logic.check_fx_estimate_update). The Help Center
  // describes that to the owner and the backend tests assert it. The block also
  // made recurring rent impossible to set up, which is the workflow that
  // surfaced it — a lease is paid on the 1st of a month that has not happened
  // yet. Owner ruling 2026-08-15. (ZEN-109)
  //
  // Money dates are now soft-future EVERYWHERE — gifts, envelopes, advances,
  // scholarships and now expenses all warn rather than block. The currentYear+1
  // ceiling above still applies and is what actually catches a mistyped year;
  // it is NOT relaxed, because it is coupled to the tax-year field, which
  // cannot exceed next year (there is no filing a year that has not started).

  // Soft warnings
  const warnings = [];

  // Soft-future date: amber warn if beyond today, but allow save
  if (opts.preset === "softFutureDate" && iso > todayIso()) {
    warnings.push("This date is in the future \u2014 confirm it\u2019s correct.");
  }

  // Completion date: warn if more than 5 years in the future
  if (isCompletion) {
    const fiveYearsOut = new Date(cy + FORWARD_WARN_YEARS, now().getMonth(), now().getDate());
    if (probe > fiveYearsOut) {
      warnings.push("This is more than 5 years out \u2014 confirm it\u2019s accurate.");
    }
  }

  // Stale (not for completion dates — a past completion is stale but not suspect)
  if (!isCompletion && y < cy - STALE_YEARS) {
    warnings.push(`${y} is more than ${STALE_YEARS} years ago. Is that right?`);
  }

  // Date↔taxYear mismatch (not applicable to completion dates)
  if (!isCompletion && opts.assignedTaxYear != null && Number.isFinite(opts.assignedTaxYear) && y !== opts.assignedTaxYear) {
    warnings.push(`This date is in ${y}, but the tax year is ${opts.assignedTaxYear}.`);
  }

  return { error: null, warning: warnings.length ? warnings.join(" ") : null };
}

// Re-export constants for tests / display
export { FLOOR_YEAR, STALE_YEARS, FORWARD_HORIZON, FORWARD_WARN_YEARS };
